import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import CompetitionsPage from "@/pages/CompetitionsPage";
import CompetitionDetailPage from "@/pages/CompetitionDetailPage";
import TeamsPage from "@/pages/TeamsPage";
import TeamDetailPage from "@/pages/TeamDetailPage";
import FixturesPage from "@/pages/FixturesPage";
import NewsPage from "@/pages/NewsPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import MediaPage from "@/pages/MediaPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import MatchesLandingPage from "@/pages/MatchesLandingPage";
import MatchCentrePage from "@/pages/MatchCentrePage";
import PlayerDetailPage from "@/pages/PlayerDetailPage";
import PlayersDirectoryPage from "@/pages/PlayersDirectoryPage";
import { TicketsPage, ShopPage, StatisticsPage, ResultsPage, TablesPage } from "@/pages/ComingSoonPage";
import NationalTeamsPage from "@/pages/NationalTeamsPage";
import NationalTeamDetailPage from "@/pages/NationalTeamDetailPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTeams from "@/pages/admin/AdminTeams";
import AdminPlayers from "@/pages/admin/AdminPlayers";
import AdminCompetitions from "@/pages/admin/AdminCompetitions";
import AdminMatches from "@/pages/admin/AdminMatches";
import AdminNews from "@/pages/admin/AdminNews";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminManagers from "@/pages/admin/AdminManagers";
import AdminPages from "@/pages/admin/AdminPages";
import AdminTeamAssignments from "@/pages/admin/AdminTeamAssignments";
import AdminDataQuality from "@/pages/admin/AdminDataQuality";
import { LaunchGate } from "@/components/LaunchGate";
import { InstallPrompt } from "@/components/InstallPrompt";
import BigScreenPage from "@/pages/BigScreenPage";
import StadiumViewPage from "@/pages/StadiumViewPage";
import BigScreenControl from "@/pages/BigScreenControl";
import AdminScheduleImport from "@/pages/admin/AdminScheduleImport";
import AdminSeasonRollover from "@/pages/admin/AdminSeasonRollover";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminStaff from "@/pages/admin/AdminStaff";
import ManagerDashboard from "@/pages/manager/ManagerDashboard";
import ManagerPlayers from "@/pages/manager/ManagerPlayers";
import ManagerMatches from "@/pages/manager/ManagerMatches";

// Components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import VisibilityGate from "@/components/VisibilityGate";
import { PageVisibilityProvider } from "@/context/PageVisibilityContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Navigate to password login page
    window.location.href = '/login';
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - detect during render, not useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<><Header /><HomePage /><Footer /></>} />
      <Route path="/about" element={<><Header /><VisibilityGate pageKey="about"><AboutPage /></VisibilityGate><Footer /></>} />
      <Route path="/competitions" element={<><Header /><VisibilityGate pageKey="competitions"><CompetitionsPage /></VisibilityGate><Footer /></>} />
      <Route path="/competitions/:competitionId" element={<><Header /><VisibilityGate pageKey="competitions"><CompetitionDetailPage /></VisibilityGate><Footer /></>} />
      <Route path="/teams" element={<><Header /><VisibilityGate pageKey="teams"><TeamsPage /></VisibilityGate><Footer /></>} />
      <Route path="/teams/:teamId" element={<><Header /><VisibilityGate pageKey="teams"><TeamDetailPage /></VisibilityGate><Footer /></>} />
      <Route path="/fixtures" element={<><Header /><FixturesPage /><Footer /></>} />
      <Route path="/news" element={<><Header /><VisibilityGate pageKey="news"><NewsPage /></VisibilityGate><Footer /></>} />
      <Route path="/news/:articleId" element={<><Header /><VisibilityGate pageKey="news"><NewsDetailPage /></VisibilityGate><Footer /></>} />
      <Route path="/media" element={<><Header /><VisibilityGate pageKey="media"><MediaPage /></VisibilityGate><Footer /></>} />
      <Route path="/contact" element={<><Header /><VisibilityGate pageKey="contact"><ContactPage /></VisibilityGate><Footer /></>} />
      <Route path="/matches" element={<><Header /><VisibilityGate pageKey="matches"><MatchesLandingPage /></VisibilityGate><Footer /></>} />
      <Route path="/match-centre/:matchId/big-screen" element={<BigScreenPage />} />
      <Route path="/match/:id/stadium" element={<StadiumViewPage />} />
      <Route path="/big-screen/screen/:screenId" element={<BigScreenPage />} />
      <Route path="/big-screen" element={<ProtectedRoute allowRoles={["admin", "big_screen_operator"]}><BigScreenControl /></ProtectedRoute>} />
      <Route path="/matches/:matchId" element={<><Header /><VisibilityGate pageKey="matches"><MatchCentrePage /></VisibilityGate><Footer /></>} />
      <Route path="/results" element={<><Header /><VisibilityGate pageKey="results"><ResultsPage /></VisibilityGate><Footer /></>} />
      <Route path="/tables" element={<><Header /><VisibilityGate pageKey="tables"><TablesPage /></VisibilityGate><Footer /></>} />
      <Route path="/players" element={<><Header /><VisibilityGate pageKey="players"><PlayersDirectoryPage /></VisibilityGate><Footer /></>} />
      <Route path="/players/:playerId" element={<><Header /><VisibilityGate pageKey="players"><PlayerDetailPage /></VisibilityGate><Footer /></>} />
      <Route path="/statistics" element={<><Header /><VisibilityGate pageKey="statistics"><StatisticsPage /></VisibilityGate><Footer /></>} />
      <Route path="/national-teams" element={<><Header /><VisibilityGate pageKey="national_teams"><NationalTeamsPage /></VisibilityGate><Footer /></>} />
      <Route path="/national-teams/:teamId" element={<><Header /><VisibilityGate pageKey="national_teams"><NationalTeamDetailPage /></VisibilityGate><Footer /></>} />
      <Route path="/tickets" element={<><Header /><VisibilityGate pageKey="tickets"><TicketsPage /></VisibilityGate><Footer /></>} />
      <Route path="/shop" element={<><Header /><VisibilityGate pageKey="shop"><ShopPage /></VisibilityGate><Footer /></>} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute area="staff"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/teams" element={<ProtectedRoute area="data"><AdminTeams /></ProtectedRoute>} />
      <Route path="/admin/players" element={<ProtectedRoute area="data"><AdminPlayers /></ProtectedRoute>} />
      <Route path="/admin/competitions" element={<ProtectedRoute area="data"><AdminCompetitions /></ProtectedRoute>} />
      <Route path="/admin/matches" element={<ProtectedRoute area="matches"><AdminMatches /></ProtectedRoute>} />
      <Route path="/admin/news" element={<ProtectedRoute area="news"><AdminNews /></ProtectedRoute>} />
      <Route path="/admin/media" element={<ProtectedRoute area="news"><AdminMedia /></ProtectedRoute>} />
      <Route path="/admin/staff" element={<ProtectedRoute area="super"><AdminStaff /></ProtectedRoute>} />
      <Route path="/admin/managers" element={<ProtectedRoute requireAdmin><AdminManagers /></ProtectedRoute>} />
      <Route path="/admin/pages" element={<ProtectedRoute requireAdmin><AdminPages /></ProtectedRoute>} />
      <Route path="/admin/team-assignments" element={<ProtectedRoute area="data"><AdminTeamAssignments /></ProtectedRoute>} />
      <Route path="/admin/data-quality" element={<ProtectedRoute requireAdmin><AdminDataQuality /></ProtectedRoute>} />
      <Route path="/admin/schedule-import" element={<ProtectedRoute area="matches"><AdminScheduleImport /></ProtectedRoute>} />
      <Route path="/admin/season-rollover" element={<ProtectedRoute requireAdmin><AdminSeasonRollover /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute requireAdmin><AdminAuditLog /></ProtectedRoute>} />
      
      {/* Team Manager Routes */}
      <Route path="/manager" element={<ProtectedRoute><ManagerDashboard /></ProtectedRoute>} />
      <Route path="/manager/players" element={<ProtectedRoute><ManagerPlayers /></ProtectedRoute>} />
      <Route path="/manager/matches" element={<ProtectedRoute><ManagerMatches /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <BrowserRouter>
        <AuthProvider>
          <PageVisibilityProvider>
            <LaunchGate><AppRouter /></LaunchGate>
            <Toaster position="top-right" richColors />
            <InstallPrompt />
          </PageVisibilityProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
